(() => {
  if (window.__sizeGuideInitialized) return;
  window.__sizeGuideInitialized = true;

  const instances = new Map();
  const dataCache = new Map();

  class SizeGuide {
    constructor(root) {
      this.root = root;
      this.panelId = root.dataset.sizeGuideId;
      this.productId = root.dataset.sizeGuideProductId;
      this.defaultUnit = root.dataset.defaultUnit || "in";
      this.defaultFitNote = root.dataset.defaultFitNote || "";
      this.assetUrl = root.dataset.sizeGuideAssetUrl || "";
      this.status = root.querySelector("[data-size-guide-status]");
      this.error = root.querySelector("[data-size-guide-error]");
      this.body = root.querySelector("[data-size-guide-body]");
      this.panelsMount = root.querySelector("[data-size-guide-panels]");
      this.tablistMount = root.querySelector("[data-size-guide-tablist-wrapper]");
      this.measureGuide = root.querySelector("[data-size-guide-measure-guide]");
      this.measureOutput = root.querySelector("[data-size-guide-measure-output]");
      this.panelElement = document.getElementById(this.panelId);
      this.inlineDataNode = root.querySelector("[data-size-guide-data]");
      this.measureNode = root.querySelector("[data-size-guide-measures]");
      this.measureGuides = this.parseJson(this.measureNode?.textContent, []);
      this.unitButtons = Array.from(
        root.querySelectorAll("[data-size-guide-unit]")
      );
      this.state = {
        unit: this.defaultUnit,
      };
      this.data = null;
      this.initialized = false;
      this.renderPromise = null;

      this.handleUnitToggle = this.handleUnitToggle.bind(this);
      this.handlePanelOpen = this.handlePanelOpen.bind(this);

      this.unitButtons.forEach((button) =>
        button.addEventListener("click", this.handleUnitToggle)
      );

      if (this.panelElement) {
        this.panelElement.addEventListener("open", this.handlePanelOpen);
      }
    }

    handlePanelOpen() {
      this.ensureRendered();
    }

    open(opener) {
      if (!this.panelElement) return;

      if (typeof this.panelElement.show === "function") {
        this.panelElement.show(opener);
      } else if (typeof this.panelElement.openDrawer === "function") {
        this.panelElement.openDrawer(opener);
      }
    }

    async ensureRendered() {
      if (this.initialized) return;
      if (this.renderPromise) {
        await this.renderPromise;
        return;
      }

      this.renderPromise = this.loadData()
        .then((data) => {
          if (!data || !Array.isArray(data.tabs) || !data.tabs.length) {
            throw new Error("No size-guide tabs found");
          }

          this.data = data;
          this.render();
          this.initialized = true;
        })
        .catch(() => {
          this.showError();
        })
        .finally(() => {
          this.renderPromise = null;
        });

      await this.renderPromise;
    }

    async loadData() {
      if (this.data) return this.data;

      const inlineText = this.inlineDataNode?.textContent?.trim();
      if (inlineText) {
        return this.parseJson(inlineText, null);
      }

      if (!this.assetUrl) return null;

      if (!dataCache.has(this.assetUrl)) {
        dataCache.set(
          this.assetUrl,
          fetch(this.assetUrl, { credentials: "same-origin" }).then(
            async (response) => {
              if (!response.ok) {
                throw new Error("Failed to fetch size guide data");
              }
              return response.json();
            }
          )
        );
      }

      return dataCache.get(this.assetUrl);
    }

    render() {
      this.hideError();
      if (this.status) this.status.hidden = true;
      if (this.body) this.body.hidden = false;

      this.tablistMount.innerHTML = "";
      this.panelsMount.innerHTML = "";

      const tablist = document.createElement("div");
      tablist.className = "f-tabs__header";
      tablist.setAttribute("role", "tablist");
      tablist.setAttribute("aria-label", "Size guide categories");

      const tablistGroup = document.createElement("div");
      tablistGroup.className = "f-tabs__header-group size-guide__tablist";
      tablist.appendChild(tablistGroup);

      const topButtons = [];
      const topPanels = [];

      this.data.tabs.forEach((tab, tabIndex) => {
        const button = this.createTabButton({
          id: `${this.panelId}-tab-${tab.id}`,
          label: tab.label,
          controls: `${this.panelId}-panel-${tab.id}`,
          isActive: tabIndex === 0,
        });
        topButtons.push(button);
        tablistGroup.appendChild(button);

        const panel = document.createElement("section");
        panel.className = "size-guide__panel";
        panel.id = `${this.panelId}-panel-${tab.id}`;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", button.id);
        if (tabIndex !== 0) panel.hidden = true;

        const subsectionUI = this.buildSubsections(tab, panel.id);
        panel.appendChild(subsectionUI.wrapper);

        topPanels.push(panel);
        this.panelsMount.appendChild(panel);
      });

      this.tablistMount.appendChild(tablist);
      this.bindTabGroup(topButtons, topPanels, (panel) => {
        const activeSubsection = panel.querySelector(
          ".size-guide__subsection-panel:not([hidden])"
        );
        if (activeSubsection) {
          this.updateMeasureGuide(activeSubsection._measureLabels || []);
        }
      });
    }

    buildSubsections(tab, parentId) {
      const subsections = safeArray(tab.subsections);
      const showSubtabs = subsections.length > 1;
      const wrapper = document.createElement("div");
      wrapper.className = "size-guide__subsections";

      const nav = document.createElement("div");
      nav.className = "size-guide__subtabs";
      nav.setAttribute("role", "tablist");
      nav.setAttribute("aria-label", `${tab.label} categories`);

      const panels = document.createElement("div");
      panels.className = "size-guide__subsection-panels";

      const subsectionButtons = [];
      const subsectionPanels = [];

      subsections.forEach((subsection, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "size-guide__subtab";
        button.id = `${parentId}-subtab-${subsection.id}`;
        button.textContent = subsection.label;
        button.setAttribute("role", "tab");
        button.setAttribute(
          "aria-selected",
          index === 0 ? "true" : "false"
        );
        button.setAttribute(
          "aria-controls",
          `${parentId}-subpanel-${subsection.id}`
        );
        button.setAttribute("tabindex", index === 0 ? "0" : "-1");
        if (showSubtabs) {
          nav.appendChild(button);
        }
        subsectionButtons.push(button);

        const panel = document.createElement("div");
        panel.className = "size-guide__subsection-panel";
        panel.id = `${parentId}-subpanel-${subsection.id}`;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", button.id);
        if (index !== 0) panel.hidden = true;

        if (showSubtabs && subsection.label) {
          const heading = document.createElement("h3");
          heading.className = "size-guide__subsection-title h5";
          heading.textContent = subsection.label;
          panel.appendChild(heading);
        }

        const tables = document.createElement("div");
        tables.className = "size-guide__table-grid";

        const measureLabels = new Set();
        subsection.tables.forEach((table) => {
          tables.appendChild(this.buildTable(table));
          safeArray(table.rows).forEach((row) => {
            if (row?.label) measureLabels.add(row.label);
          });
        });

        panel._measureLabels = Array.from(measureLabels);
        panel.appendChild(tables);
        panels.appendChild(panel);
        subsectionPanels.push(panel);
      });

      if (showSubtabs) {
        wrapper.appendChild(nav);
      }
      wrapper.appendChild(panels);

      if (showSubtabs) {
        this.bindTabGroup(subsectionButtons, subsectionPanels, (panel) => {
          this.updateMeasureGuide(panel._measureLabels || []);
        });
      } else if (subsectionPanels[0]) {
        this.updateMeasureGuide(subsectionPanels[0]._measureLabels || []);
      }

      return { wrapper };
    }

    buildTable(table) {
      const templateType =
        table.table_type === "accessory" ? "accessory" : "apparel";
      const template = this.root.querySelector(
        `[data-size-guide-template="${templateType}"]`
      );
      const fragment = template.content.firstElementChild.cloneNode(true);
      const sizeLabels = safeArray(table.size_labels).filter(Boolean);
      const fitNote = table.fit_note || this.defaultFitNote;

      const styleNode = fragment.querySelector("[data-size-guide-style-code]");
      if (table.style_code) {
        styleNode.hidden = false;
        styleNode.textContent = `Style ${table.style_code}`;
      } else {
        styleNode.hidden = true;
      }
      fragment.querySelector("[data-size-guide-table-title]").textContent =
        table.title || table.style_code;

      const categoryNode = fragment.querySelector(
        "[data-size-guide-table-category]"
      );
      if (table.category) {
        categoryNode.hidden = false;
        categoryNode.textContent = table.category;
      }

      const fitNoteNode = fragment.querySelector("[data-size-guide-fit-note]");
      if (fitNote) {
        fitNoteNode.hidden = false;
        fitNoteNode.textContent = `Fit note: ${fitNote}`;
      }

      const thead = fragment.querySelector("[data-size-guide-head]");
      const tbody = fragment.querySelector("[data-size-guide-body]");
      const headRow = document.createElement("tr");
      const measureHead = document.createElement("th");
      measureHead.scope = "col";
      measureHead.className = "size-guide__table-label";
      measureHead.textContent = "Measurement";
      headRow.appendChild(measureHead);

      sizeLabels.forEach((label) => {
        const th = document.createElement("th");
        th.scope = "col";
        th.textContent = label;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);

      safeArray(table.rows).forEach((row) => {
        const tr = document.createElement("tr");
        const rowHeader = document.createElement("th");
        rowHeader.scope = "row";
        rowHeader.textContent = row.label;
        tr.appendChild(rowHeader);

        sizeLabels.forEach((sizeLabel) => {
          const td = document.createElement("td");
          const cmValue = row?.cm?.[sizeLabel] || "";
          const inValue = row?.in?.[sizeLabel] || "";
          td.dataset.sizeGuideValue = "";
          td.dataset.cm = cmValue;
          td.dataset.in = inValue;
          td.textContent =
            (this.state.unit === "cm" ? cmValue : inValue) || "—";
          tr.appendChild(td);
        });

        tbody.appendChild(tr);
      });

      return fragment;
    }

    bindTabGroup(buttons, panels, onChange) {
      if (!buttons.length || !panels.length) return;

      const setActive = (index, shouldFocus = false) => {
        buttons.forEach((button, buttonIndex) => {
          const isActive = buttonIndex === index;
          button.setAttribute("aria-selected", isActive ? "true" : "false");
          button.setAttribute("tabindex", isActive ? "0" : "-1");
          button.classList.toggle("is-active", isActive);
          panels[buttonIndex].hidden = !isActive;
        });

        if (shouldFocus) buttons[index].focus();
        if (typeof onChange === "function") onChange(panels[index], buttons[index]);
      };

      buttons.forEach((button, index) => {
        button.addEventListener("click", () => setActive(index));
        button.addEventListener("keydown", (event) => {
          const key = event.key;
          if (
            key !== "ArrowRight" &&
            key !== "ArrowLeft" &&
            key !== "Home" &&
            key !== "End"
          ) {
            return;
          }

          event.preventDefault();

          let nextIndex = index;
          if (key === "ArrowRight") {
            nextIndex = (index + 1) % buttons.length;
          } else if (key === "ArrowLeft") {
            nextIndex = (index - 1 + buttons.length) % buttons.length;
          } else if (key === "Home") {
            nextIndex = 0;
          } else if (key === "End") {
            nextIndex = buttons.length - 1;
          }

          setActive(nextIndex, true);
        });
      });

      setActive(0);
    }

    handleUnitToggle(event) {
      const nextUnit = event.currentTarget.dataset.sizeGuideUnit;
      if (!nextUnit || nextUnit === this.state.unit) return;

      this.state.unit = nextUnit;
      this.unitButtons.forEach((button) => {
        const isActive = button.dataset.sizeGuideUnit === nextUnit;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      this.root.querySelectorAll("[data-size-guide-value]").forEach((cell) => {
        const value = cell.dataset[nextUnit] || "";
        cell.textContent = value || "—";
      });
    }

    updateMeasureGuide(labels) {
      if (!this.measureGuide || !this.measureOutput) return;

      const normalizedLabels = labels.map((label) => normalizeText(label));
      const matches = this.measureGuides.filter((guide) => {
        const aliases = safeArray(guide.aliases);
        const targets = [guide.title, ...aliases].map((item) =>
          normalizeText(item)
        );
        return targets.some((target) =>
          normalizedLabels.some(
            (label) => label.includes(target) || target.includes(label)
          )
        );
      });

      this.measureOutput.innerHTML = "";
      if (!matches.length) {
        this.measureGuide.hidden = true;
        return;
      }

      matches.forEach((guide) => {
        const card = document.createElement("article");
        card.className = "size-guide__measure-card";

        const title = document.createElement("h4");
        title.className = "size-guide__measure-card-title h6";
        title.textContent = guide.title;

        const description = document.createElement("p");
        description.className = "text-sm";
        description.textContent = guide.description;

        card.appendChild(title);
        card.appendChild(description);
        this.measureOutput.appendChild(card);
      });

      this.measureGuide.hidden = false;
    }

    createTabButton({ id, label, controls, isActive }) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "f-tabs__nav size-guide__tab";
      button.id = id;
      button.textContent = label;
      button.setAttribute("role", "tab");
      button.setAttribute("aria-controls", controls);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      button.setAttribute("tabindex", isActive ? "0" : "-1");
      return button;
    }

    parseJson(value, fallbackValue) {
      if (!value) return fallbackValue;

      try {
        return JSON.parse(value);
      } catch (error) {
        return fallbackValue;
      }
    }

    showError() {
      if (this.status) this.status.hidden = true;
      if (this.body) this.body.hidden = true;
      if (this.error) this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }

  function safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  function initGuides(scope = document) {
    scope.querySelectorAll("[data-size-guide-root]").forEach((root) => {
      const panelId = root.dataset.sizeGuideId;
      if (!panelId || instances.has(panelId)) return;
      instances.set(panelId, new SizeGuide(root));
    });
  }

  function getGuideForTrigger(trigger) {
    const targetId = trigger.dataset.sizeGuideTarget;
    if (targetId && instances.has(targetId)) {
      return instances.get(targetId);
    }

    const productId = trigger.dataset.sizeGuideProductId;
    if (!productId) return null;

    for (const instance of instances.values()) {
      if (instance.productId === productId) return instance;
    }

    return null;
  }

  function getLegacyModal(trigger) {
    const selector = trigger.dataset.legacyModal;
    if (!selector) return null;
    return document.querySelector(selector);
  }

  function syncExternalTriggers(scope = document) {
    scope.querySelectorAll("[data-size-guide-open]").forEach((trigger) => {
      if (trigger.dataset.sizeGuideTarget) return;

      const shouldShow = Boolean(getGuideForTrigger(trigger) || getLegacyModal(trigger));
      trigger.hidden = !shouldShow;

      if (shouldShow) {
        trigger.removeAttribute("aria-hidden");
      } else {
        trigger.setAttribute("aria-hidden", "true");
      }
    });
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-size-guide-open]");
    if (!trigger) return;

    const guide = getGuideForTrigger(trigger);
    const legacyModal = getLegacyModal(trigger);
    if (!guide && !legacyModal) return;

    event.preventDefault();

    if (guide) {
      guide.open(trigger);
      return;
    }

    if (legacyModal && typeof legacyModal.show === "function") {
      legacyModal.show(trigger);
    }
  });

  document.addEventListener("DOMContentLoaded", () => {
    initGuides(document);
    syncExternalTriggers(document);
  });

  document.addEventListener("shopify:section:load", (event) => {
    initGuides(event.target);
    syncExternalTriggers(document);
  });
})();
